import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { SizeContext } from '../utils/SizeContext';
import MainView from './BookingFlow/MainView';
import CalendarView from './BookingFlow/Calendar/CalendarView';
import FullyBookedView from './BookingFlow/FullyBooked/FullyBookedView';
import AdultConfirmationView from './BookingFlow/AdultBooking/AdultConfirmationView';
import { BookingFlowViews, BookingState } from './BookingFlow/types';
import { useAppointmentSlots, usePracticeSettings } from './BookingFlow/hooks';
import LoadingView from '../components/Loading/LoadingView';
import { bookMeeting } from '../api/api';
import { useErrorHandler } from './Error/ErrorBoundary';
import { trackEvent } from '../utils/eventTracking';
import { BOOKING_HORIZON_DAYS } from '../constants';
import { RadioOption } from '../widget-ui-toolkit/RadioButton/types';
import ChildConfirmationView from './BookingFlow/ChildBooking/ChildConfirmationView';
import CarerView from './BookingFlow/Carer/CarerView';
import styles from './BookingFlow/MainView.module.css';
import FormattedMessage from '../widget-ui-toolkit/FormattedMessage';
import CarerTypeDeactivatedView from './BookingFlow/Carer/CarerTypeDeactivatedView';
import CarerTypeUnavailableView from './BookingFlow/Carer/CarerTypeUnavailableView';
import { SlotsAndUnavailability } from '../components/TimeScheduleInput/types';

const LOCALIZE_PREFIX = 'widget.booking.loading';

type BookingFlowProps = { onCloseContentView: () => void };

const BookingFlow = ({
  onCloseContentView,
}: BookingFlowProps): ReactElement => {
  const practiceSettings = usePracticeSettings(true);
  const { nurseSlots, doctorSlots, refetch } = useAppointmentSlots();

  const [nurseAppointmentDateTime, setNurseAppointmentDateTime] =
    useState<Date>();
  const [doctorAppointmentDateTime, setDoctorAppointmentDateTime] =
    useState<Date>();

  const [currentView, setCurrentView] = useState<BookingFlowViews | undefined>(
    BookingFlowViews.Loading
  );
  const [isSlotUnavailable, setSlotUnavailable] = useState(false);
  const [booking, setBooking] = useState<BookingState>({
    timeSlot: new Date(),
    phoneNumber: '',
    optionChecked: false,
    legalGuardianCheckboxChecked: false,
  });

  const [patientTypeOption, setPatientTypeOption] = useState<RadioOption>({
    id: -1,
    label: '',
    value: undefined,
  });

  const [showNurse, setShowNurse] = useState(false);
  const [showDoctor, setShowDoctor] = useState(true);

  function displayNurse() {
    setShowNurse(true);
    setShowDoctor(false);
    booking.professionId = 'nurse';
    if (nurseSlots?.availableSlots.length === 0) {
      if (!practiceSettings.isNurseBookingEnabled) {
        changeView(BookingFlowViews.CarerDeactivated);
      } else {
        changeView(BookingFlowViews.CarerUnavailable);
      }
    } else if (currentView !== BookingFlowViews.Calendar)
      changeView(BookingFlowViews.Main);
  }

  function displayDoctor() {
    setShowDoctor(true);
    setShowNurse(false);
    booking.professionId = 'doctor';
    if (doctorSlots?.availableSlots.length === 0) {
      changeView(BookingFlowViews.CarerUnavailable);
    } else if (currentView !== BookingFlowViews.Calendar) {
      changeView(BookingFlowViews.Main);
    }
  }

  const onSizeUpdate = useContext(SizeContext);
  const handleError = useErrorHandler();

  function changeView(view: BookingFlowViews) {
    setCurrentView(view);
    onSizeUpdate();
  }

  const updateNurseBookingTime = (newDate: Date) => {
    setNurseAppointmentDateTime(newDate);
    changeView(BookingFlowViews.Main);
  };

  const updateDoctorBookingTime = (newDate: Date) => {
    setDoctorAppointmentDateTime(newDate);
    changeView(BookingFlowViews.Main);
  };

  const handleClose = () => {
    onCloseContentView();
    setCurrentView(undefined);
    setBooking({} as BookingState);
  };

  const bookAppointment = async () => {
    setCurrentView(BookingFlowViews.Loading);
    try {
      const response = await bookMeeting(booking);
      if (response) {
        changeView(BookingFlowViews.Confirmation);
      }
    } catch (error: any) {
      if (error.status === 400) {
        setSlotUnavailable(true);
        trackEvent({ action: 'slot-taken-error-displayed' });
        changeView(BookingFlowViews.Main);
      } else {
        handleError(error);
      }
    }
  };

  const handleSelectNewAppointmentDateTime = async () => {
    if (isSlotUnavailable) {
      await refetch();
    }
    changeView(BookingFlowViews.Calendar);
    setSlotUnavailable(false);
  };

  function handleAppointmentDateTimeAndView(
    checkingNurseSlots: boolean,
    slots: SlotsAndUnavailability | undefined,
    fn: (date: Date | undefined) => void
  ) {
    if (slots) {
      if (slots.availableSlots.length > 0) {
        fn(slots.availableSlots.find((s) => !s.disabled)?.date);
      } else {
        if (!practiceSettings.isNurseBookingEnabled && checkingNurseSlots) {
          changeView(BookingFlowViews.CarerDeactivated);
        } else {
          changeView(BookingFlowViews.CarerUnavailable);
        }
      }
    }
  }

  function notAnyAvailability() {
    return (
      nurseSlots &&
      !nurseSlots.availableSlots.length &&
      doctorSlots &&
      !doctorSlots.availableSlots.length
    );
  }

  useEffect(() => {
    if (notAnyAvailability()) {
      setCurrentView(BookingFlowViews.FullyBooked);
    } else {
      handleAppointmentDateTimeAndView(
        true,
        nurseSlots,
        setNurseAppointmentDateTime
      );
      handleAppointmentDateTimeAndView(
        false,
        doctorSlots,
        setDoctorAppointmentDateTime
      );
    }
    onSizeUpdate();
  }, [nurseSlots, doctorSlots, onSizeUpdate]);

  function displayMainViewForCarerAvailability(
    slots: SlotsAndUnavailability | undefined,
    showCarer: boolean
  ) {
    if (slots && slots.availableSlots.length > 0 && showCarer) {
      changeView(BookingFlowViews.Main);
    }
  }

  useEffect(() => {
    displayMainViewForCarerAvailability(nurseSlots, showNurse);

    setBooking({ ...booking, timeSlot: nurseAppointmentDateTime });
    onSizeUpdate();
  }, [nurseAppointmentDateTime]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    displayMainViewForCarerAvailability(doctorSlots, showDoctor);

    setBooking({ ...booking, timeSlot: doctorAppointmentDateTime });
    onSizeUpdate();
  }, [doctorAppointmentDateTime]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setBooking({ ...booking });
  }, []);

  useEffect(() => {
    setBooking({ ...booking, isChild: patientTypeOption.value });
  }, [patientTypeOption.value]);

  return (
    <>
      {(currentView === BookingFlowViews.Main ||
        currentView === BookingFlowViews.CarerUnavailable ||
        currentView === BookingFlowViews.CarerDeactivated) && (
        <>
          <label
            htmlFor="labelForAppointmentType"
            className={styles.textInputLabel}
          >
            <FormattedMessage id="Appointment type" />
          </label>

          <CarerView
            showNurse={showNurse}
            showDoctor={showDoctor}
            onNurseClick={() => displayNurse()}
            onDoctorClick={() => displayDoctor()}
          />
        </>
      )}
      {currentView === BookingFlowViews.Loading && (
        <LoadingView
          message={
            booking.optionChecked ? '' : `${LOCALIZE_PREFIX}.loading_message`
          }
        />
      )}
      {currentView === BookingFlowViews.FullyBooked && <FullyBookedView />}
      {currentView === BookingFlowViews.CarerUnavailable && (
        <CarerTypeUnavailableView />
      )}
      {currentView === BookingFlowViews.CarerDeactivated && (
        <CarerTypeDeactivatedView />
      )}
      {currentView === BookingFlowViews.Main &&
        nurseAppointmentDateTime &&
        showNurse && (
          <MainView
            booking={booking}
            onChangeBooking={setBooking}
            onSelectNewAppointmentDateTime={handleSelectNewAppointmentDateTime}
            appointmentDateTime={nurseAppointmentDateTime}
            onBookAppointment={bookAppointment}
            isSlotUnavailable={isSlotUnavailable}
            partnership={practiceSettings.partnership}
            patientTypeOption={patientTypeOption}
            setPatientTypeOption={setPatientTypeOption}
            showNurse={showNurse}
          />
        )}

      {currentView === BookingFlowViews.Main &&
        doctorAppointmentDateTime &&
        showDoctor && (
          <MainView
            booking={booking}
            onChangeBooking={setBooking}
            onSelectNewAppointmentDateTime={handleSelectNewAppointmentDateTime}
            appointmentDateTime={doctorAppointmentDateTime}
            onBookAppointment={bookAppointment}
            isSlotUnavailable={isSlotUnavailable}
            partnership={practiceSettings.partnership}
            patientTypeOption={patientTypeOption}
            setPatientTypeOption={setPatientTypeOption}
            showNurse={showNurse}
          />
        )}

      {currentView === BookingFlowViews.Calendar &&
        nurseAppointmentDateTime &&
        showNurse &&
        nurseSlots?.availableSlots && (
          <CalendarView
            slots={nurseSlots}
            appointmentDateTime={nurseAppointmentDateTime}
            onSubmit={updateNurseBookingTime}
            onCancel={() => changeView(BookingFlowViews.Main)}
            bookingHorizonDays={BOOKING_HORIZON_DAYS}
            showNurse={showNurse}
            showDoctor={showDoctor}
            onNurseClick={() => displayNurse()}
            onDoctorClick={() => displayDoctor()}
          />
        )}

      {currentView === BookingFlowViews.Calendar &&
        doctorAppointmentDateTime &&
        showDoctor &&
        doctorSlots?.availableSlots && (
          <CalendarView
            slots={doctorSlots}
            appointmentDateTime={doctorAppointmentDateTime}
            onSubmit={updateDoctorBookingTime}
            onCancel={() => changeView(BookingFlowViews.Main)}
            bookingHorizonDays={BOOKING_HORIZON_DAYS}
            showNurse={showNurse}
            showDoctor={showDoctor}
            onNurseClick={() => displayNurse()}
            onDoctorClick={() => displayDoctor()}
          />
        )}
      {currentView === BookingFlowViews.Confirmation && !booking.isChild && (
        <AdultConfirmationView onClose={handleClose} booking={booking} />
      )}
      {currentView === BookingFlowViews.Confirmation && booking.isChild && (
        <ChildConfirmationView onClose={handleClose} booking={booking} />
      )}
    </>
  );
};

export default BookingFlow;
